/**
 * An Analytics panel draws its data in one of several shapes, picked by `config.display.type`.
 * This file publishes the declared types as the lists the API and the Cirra frontend read. Each
 * type is declared in `panelTypes/`, one file per renderer, and the order there is the order here.
 */
import analyticsPanelRenderers from './panelTypes/index.js';

/* One entry per type name, carrying the block its renderer reads, so a caller holding a stored
   type knows where that type's settings live without naming the renderer. */
export const analyticsPanelTypes = analyticsPanelRenderers.flatMap((renderer) => renderer.names.map((name) => ({ ...name, block: renderer.block })));

/* Names stored before a rename. They still arrive from saved panels, so every surface reading a
   stored type has to recognize them, but they are never offered as a choice. */
const ALIASES = { list: 'table' };

const typesById = new Map(analyticsPanelTypes.map((type) => [type.id, type]));

/* Resolves a stored type, alias included, to its entry. Unknown types return null so the caller
   decides what an unrecognized panel does. */
export const getAnalyticsPanelType = (id) => typesById.get(ALIASES[id] || id) || null;

export const ANALYTICS_PANEL_TYPE_IDS = analyticsPanelTypes.map((type) => type.id);

/* Every type display code may return, plus the aliases, since a stored alias is still a valid
   thing to return. This is what advanced-mode validation accepts. */
export const ANALYTICS_ADVANCED_DISPLAY_TYPE_IDS = [...analyticsPanelTypes.filter((type) => type.advanced).map((type) => type.id), ...Object.keys(ALIASES)];

export const ANALYTICS_PANEL_GUIDE_FILENAMES = analyticsPanelTypes.map((type) => type.guide);

/* The formats a report page's panel may take (ADR 001 of the report-formats initiative), in
   registry order. `report` (Written) may also sit on a grid page; the others exist only on report
   pages, which `ANALYTICS_REPORT_ONLY_TYPE_IDS` names. */
export const ANALYTICS_REPORT_FORMAT_TYPE_IDS = analyticsPanelTypes.filter((type) => type.reportFormat).map((type) => type.id);

export const ANALYTICS_REPORT_ONLY_TYPE_IDS = ANALYTICS_REPORT_FORMAT_TYPE_IDS.filter((id) => id !== 'report');

/* The types a panel on an ordinary page may take — every type but the report-only formats — and the
   types a report page's panel may take. The grid surfaces (panel prompt, panel tool) offer the
   first; the report surface offers the second; the panel update service enforces both. */
export const ANALYTICS_GRID_PANEL_TYPES = analyticsPanelTypes.filter((type) => !ANALYTICS_REPORT_ONLY_TYPE_IDS.includes(type.id));

export const ANALYTICS_REPORT_FORMAT_TYPES = analyticsPanelTypes.filter((type) => type.reportFormat);

/* Whether a panel's output is a REPORT (any format): cut into immutable generations with its AI
   sections written, read by publications from the newest one. */
export const isAnalyticsReportFormatPanel = (config) => ANALYTICS_REPORT_FORMAT_TYPE_IDS.includes(String(config?.display?.type || ''));

/* Companion documents living beside the guides — declared by the type they accompany (report's
   AI-snippets doc), never offered in the per-type guide index. Exists so the guide-directory
   coverage test can hold "every file on disk is declared somewhere" without a hand-kept
   exception list; the snippets doc shipped undeclared (2026-08-24) and that test went red. */
/* Deduplicated: two types may share a companion (Written and Document reports both take the
   AI-sections doc). */
export const ANALYTICS_PANEL_COMPANION_GUIDE_FILENAMES = [...new Set(analyticsPanelTypes.flatMap((type) => (Array.isArray(type.companionGuides) ? type.companionGuides : [])))];

/* The gestures a stored type's renderer fires, from the type's own declaration — the enum the
   panel tool offers and the list a click editor shows. A type that fires none returns [], which is
   how a display type without interactions is honestly described rather than silently interactive. */
export const getAnalyticsPanelTypeEvents = (id) => {
  const type = getAnalyticsPanelType(id);
  const renderer = type ? analyticsPanelRenderers.find((candidate) => candidate.names.some((name) => name.id === type.id)) : null;

  return Array.isArray(renderer?.events) ? renderer.events : [];
};

/* Every event any declared type fires — the vocabulary a writer may store today. Derived, so a type
   claiming a new event reaches the tool schema the moment its file lands. */
export const ANALYTICS_PANEL_WIRED_EVENT_NAMES = [...new Set(analyticsPanelRenderers.flatMap((renderer) => (Array.isArray(renderer.events) ? renderer.events : [])))];

/* Every key under `display` that holds a type's settings, in type order. This is what a surface
   walks when it has to describe or echo back a panel's whole configuration, instead of keeping its
   own list of the blocks it happens to know about. */
export const ANALYTICS_DISPLAY_BLOCK_NAMES = analyticsPanelRenderers.map((renderer) => renderer.block);
